"use client";
import {useCallback, useEffect, useMemo} from "react";
import {useCookieCampusId} from "./contexts/CampusCookieContext";
import {useUserInfo} from "@/app/_components/contexts/userInfoContext";
import getUserData from "@/app/_components/EventTracker/getUserData";
import {MIXPANEL_SUPER_PROPERTIES, useTracking} from "../../lib/Analytics";
import fetchDataForAnalytics from "@/lib/legacyApi/fetchDataForAnalytics";
import {isParent} from "@/app/util/userUtils";

/**
 * Utility component which attaches some variables to the window object for
 * analytics tagging and starts Mixpanel for event tracking.
 */
const AnalyticsVariablesSetter = () => {
	const {userInfo, isLoading} = useUserInfo();
	const campusId = useCookieCampusId();
	const {analytics} = useTracking();

	const userAnalyticsData = useMemo(
		() => (!isLoading && userInfo ? getUserData(userInfo, campusId) : null),
		[campusId, isLoading, userInfo]
	);

	const setEnrollmentProperties = useCallback(async () => {
		if (analytics && userInfo && isParent(userInfo)) {
			// Send pc enrollment data to Mixpanel for parents
			try {
				const enrollmentAnalyticsData = await fetchDataForAnalytics(
					userInfo.platform_user_id
				);
				analytics.people.set(enrollmentAnalyticsData);
			} catch (error) {
				console.error("Error fetching data for Mixpanel:", error);
			}
		}
	}, [analytics, userInfo]);

	// GTM stuff
	useEffect(() => {
		if (userAnalyticsData) {
			window.analytics = userAnalyticsData;

			// Add to `window.dataLayer` which is used directly by Google Tag Manager
			window.dataLayer?.push(window.analytics);
		}
	}, [userAnalyticsData]);

	// Mixpanel stuff
	useEffect(() => {
		if (!analytics) {
			return;
		}
		if (userInfo && userAnalyticsData) {
			analytics.identify(userInfo.platform_user_id);
			analytics.people.set(userAnalyticsData);
			// Get extra info if available
			setEnrollmentProperties();
		} else {
			// Reset is only called during logout:
			// https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript#call-reset-at-logout
			analytics.reset();
			analytics.register(MIXPANEL_SUPER_PROPERTIES);
		}
	}, [analytics, userAnalyticsData, setEnrollmentProperties, userInfo]);

	return null;
};

export default AnalyticsVariablesSetter;
